// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import setPageMetadata from "@insite/client-framework/Common/Utilities/setPageMetadata";
import Zone from "@insite/client-framework/Components/Zone";
import setBreadcrumbs from "@insite/client-framework/Store/Components/Breadcrumbs/Handlers/SetBreadcrumbs";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { DealerStateContext, getDealerState } from "@insite/client-framework/Store/Data/Dealers/DealersSelectors";
import {
    getAlternateLanguageUrls,
    getCurrentPage,
    getLocation,
} from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import displayDealer from "@insite/client-framework/Store/Pages/DealerDetails/Handlers/DisplayDealer";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { generateLinksFrom } from "@insite/content-library/Components/PageBreadcrumbs";
import { LinkProps } from "@insite/mobius/Link";
import Page from "@insite/mobius/Page";
import loadDealerId from "@mcmc/App/Handlers/Dealer/LoadDealerId";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import cloneDeep from "lodash/cloneDeep";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

interface OwnProps extends PageProps {
    dealerId: string;
}

const mapStateToProps = (state: McMcApplicationState, ownProps: OwnProps) => {
    const location = getLocation(state);
    const homePageUrl = getHomePageUrl(state);
    const page = getCurrentPage(state);
    return {
        homePageUrl,
        dealerState: getDealerState(state, ownProps.dealerId),
        links: state.links,
        dealerDetailsPageLink: getPageLinkByPageType(state, "DealerDetailsPage"),
        nodeId: page.nodeId,
        breadcrumbLinks: state.components.breadcrumbs.links,
        websiteName: state.context.website.name,
        dealerPath: location.pathname,
        websiteSettings: getSettingsCollection(state).websiteSettings,
        alternateLanguageUrls: getAlternateLanguageUrls(state, page.id),
    };
};

const mapDispatchToProps = {
    displayDealer,
    setBreadcrumbs,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface State {
    metadataUpdatedForId?: string;
}

class DealerDetails extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    UNSAFE_componentWillMount(): void {
        const { dealerId, displayDealer, breadcrumbLinks, dealerState } = this.props;
        if (dealerId && !dealerState.value) {
            displayDealer({ dealerId });
            this.setMetadata(false);
        }

        if (!breadcrumbLinks && dealerState.value) {
            this.setPageBreadcrumbs();
        }
    }

    componentDidUpdate(prevProps: Props) {
        const { dealerId, displayDealer, dealerState } = this.props;

        if (prevProps && dealerState.value && dealerState.value?.id !== prevProps.dealerState?.value?.id) {
            this.setPageBreadcrumbs();
        }

        if (dealerState.value && dealerState.value.id !== this.state.metadataUpdatedForId) {
            this.setMetadata(true);
        }

        if (dealerId && !dealerState.value) {
            displayDealer({ dealerId });
        }
    }

    componentWillUnmount() {
        this.props.setBreadcrumbs({ links: undefined });
    }

    setMetadata(isUpdate: boolean) {
        const {
            dealerState: { value: dealer },
            websiteName,
            dealerPath,
            websiteSettings,
            alternateLanguageUrls,
        } = this.props;
        if (!dealer) {
            return;
        }

        setPageMetadata(
            {
                currentPath: dealerPath,
                title: dealer.name,
                websiteName,
                alternateLanguageUrls,
            },
            websiteSettings,
        );

        if (isUpdate) {
            this.setState({
                metadataUpdatedForId: dealer.id,
            });
        }
    }

    setPageBreadcrumbs() {
        const { dealerState, links, nodeId, dealerDetailsPageLink, homePageUrl, setBreadcrumbs } = this.props;

        const breadcrumbs = generateLinksFrom(links, nodeId, homePageUrl);
        const updatedBreadcrumbs = cloneDeep(breadcrumbs) as LinkProps[];
        updatedBreadcrumbs.forEach(link => {
            link.children = link.children === dealerDetailsPageLink?.title ? dealerState.value!.name! : link.children;
        });
        setBreadcrumbs({ links: updatedBreadcrumbs });
    }

    render() {
        return (
            <Page>
                <DealerStateContext.Provider value={this.props.dealerState}>
                    <Zone contentId={this.props.id} zoneName="Content" />
                </DealerStateContext.Provider>
            </Page>
        );
    }
}

const DealerDetailsPage = connect(mapStateToProps, mapDispatchToProps)(DealerDetails);
// XNG-changes : XSD-10425: Create SEO friendly URLs on Location Detail Pages.
// The ticket required to change url to [location/state-city].
// create new service to get dealerId based on state-city(urlSegment) and passed the dealerId to dealerDetailsPage as props to continue as the previous behaviour that depends on dealerId

const XngDealerDetailsPage: FC<Props> = props => {
    const dispatch = useDispatch();
    const location = useAppSelector(o => getLocation(o));
    const [dealerId, setDealerId] = useState("");

    useEffect(() => {
        const parsedQuery = parseQueryString<{ id?: string }>(location.search);
        if (parsedQuery.id) {
            dispatch(
                loadDealerId({
                    urlSegment: parsedQuery.id,
                    onSuccess: result => {
                        if (!result) {
                            return;
                        }
                        setDealerId(result);
                    },
                }),
            );
        }
    }, []);

    if (!dealerId) {
        return null;
    }

    return <DealerDetailsPage {...props} dealerId={dealerId}></DealerDetailsPage>;
};

const pageModule: PageModule = {
    component: XngDealerDetailsPage,
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "DealerDetailsPage" instead of this constant.
 */
export const DealerDetailsPageContext = "DealerDetailsPage";
